// Background ("silent") refresh for hooks that load from a server callable.
//
// The first load for an identity/council/authorization context clears the
// hook's state first (so nothing from the previous context can be shown while
// the new answer is in flight). Later refreshes — the window regaining focus,
// or a local change event — must NOT clear anything: the current data stays
// on screen until the new response replaces it, so the page, scroll position
// and open controls are untouched. A failed background refresh keeps the data
// and reports through `onError(error, true)`, except for authorization
// failures, which are treated like a first-load failure (state cleared).
//
// Pure TypeScript with no React or Firebase imports so it can be unit tested.

/** Minimum time between focus-triggered refreshes. Local change events are never throttled. */
export const FOCUS_REFRESH_MIN_MS = 30_000;

/** Errors that mean the viewer may no longer be allowed to see this data. A background refresh that fails this way must not keep the old data. */
export function isAccessLostError(error: unknown): boolean {
  const code = typeof error === 'object' && error !== null ? (error as { code?: unknown }).code : undefined;
  return code === 'functions/permission-denied' || code === 'functions/unauthenticated' || code === 'permission-denied' || code === 'unauthenticated';
}

export function toError(error: unknown, fallback: string): Error {
  return error instanceof Error ? error : new Error(fallback);
}

export interface RefreshControllerOptions<T> {
  fetch: () => Promise<T>;
  /** Called by start() only: clear the hook's state and show the loading state. */
  onStart: () => void;
  /** A response arrived (first load or background): replace the hook's state with it. */
  onResult: (value: T) => void;
  /** A fetch failed. `background` is true only when old data is being kept; false means clear the state and show the error. */
  onError: (error: unknown, background: boolean) => void;
  focusMinIntervalMs?: number;
  now?: () => number;
}

export interface RefreshController {
  /** First load for this identity/council/authorization context (clear-first). */
  start(): void;
  /** `focus` is throttled and skipped while a fetch is running; `event` always results in a fresh fetch (queued if one is running). */
  refresh(kind: 'focus' | 'event'): void;
  /** Ignore everything still in flight. Call from the effect cleanup. */
  dispose(): void;
}

export function createRefreshController<T>(options: RefreshControllerOptions<T>): RefreshController {
  const minMs = options.focusMinIntervalMs ?? FOCUS_REFRESH_MIN_MS;
  const now = options.now ?? Date.now;
  let disposed = false;
  let inFlight = false;
  let queued = false;
  let lastStartedAt = -Infinity;

  const run = (background: boolean) => {
    inFlight = true;
    lastStartedAt = now();
    let promise: Promise<T>;
    try {
      promise = options.fetch();
    } catch (error) {
      promise = Promise.reject(error);
    }
    promise.then(
      (value) => {
        if (disposed) return;
        inFlight = false;
        options.onResult(value);
        drain();
      },
      (error: unknown) => {
        if (disposed) return;
        inFlight = false;
        options.onError(error, background && !isAccessLostError(error));
        drain();
      },
    );
  };

  const drain = () => {
    if (!queued || disposed) return;
    queued = false;
    run(true);
  };

  return {
    start() {
      if (disposed) return;
      queued = false;
      options.onStart();
      run(false);
    },
    refresh(kind) {
      if (disposed) return;
      if (kind === 'focus') {
        if (inFlight || now() - lastStartedAt < minMs) return;
        run(true);
      } else if (inFlight) {
        queued = true;
      } else {
        run(true);
      }
    },
    dispose() {
      disposed = true;
      queued = false;
    },
  };
}
